// Libraries
import { initializeApp, setLogLevel } from 'firebase/app'
import { initializeAnalytics, isSupported, setAnalyticsCollectionEnabled } from 'firebase/analytics'

// Utilities
import environment from '../utility/environmentUtils'
import logger from '../utility/loggerUtil'

class FirebaseWebUtil {
  /**
   * Allow firebase.app.App instance only to be created once.
   * @type {firebase.app.App|null}
   */
  static #app = null

  /**
   * Analytics instance, only set once initializeAnalytics() has resolved..
   * @type {import('firebase/analytics').Analytics|null}
   */
  static #analytics = null

  /**
   * Resolves init()'s readiness promise below. Declared first so it's assigned by the time the executor below runs.
   * @type {(analytics: import('firebase/analytics').Analytics|null) => void}
   */
  static #resolveReady = null

  /**
   * Resolves with the Analytics instance once init() has settled, or null if analytics ended up unavailable (unsupported browser, or init failed).
   * @type {Promise<import('firebase/analytics').Analytics|null>}
   */
  static #ready = new Promise((resolve) => {
    FirebaseWebUtil.#resolveReady = resolve
  })

  /**
   * Initialize firebase SDK.
   */
  static async init () {
    try {
      const firebaseConfig = {
        apiKey: 'AIzaSyDHFJBfdZS2DvjJZmLe1KG1JcUPoSqbq58', // Per Firebase, this key does not give access to backend systems and is safe to hardcode: https://firebase.google.com/docs/projects/api-keys
        authDomain: 'titlemax-express.firebaseapp.com',
        projectId: 'titlemax-express',
        storageBucket: 'titlemax-express.firebasestorage.app',
        messagingSenderId: '150217897590',
        appId: '1:150217897590:web:f8c363a0091103818d1157',
        measurementId: 'G-4XBPM2W0T1'
      }

      FirebaseWebUtil.#app = initializeApp(firebaseConfig, { automaticDataCollectionEnabled: true })

      if (environment.isLowerEnv()) {
        setLogLevel('verbose')
      }

      logger.debug(`Firebase instance: ${FirebaseWebUtil.#app.name}`)

      const isAnalyticsSupported = await isSupported()

      logger.debug(`Analytics supported: ${isAnalyticsSupported}`)

      if (isAnalyticsSupported) {
        FirebaseWebUtil.#analytics = initializeAnalytics(FirebaseWebUtil.#app, { allow_ad_personalization_signals: true, allow_google_signals: true })
        setAnalyticsCollectionEnabled(FirebaseWebUtil.#analytics, true)
      }
    } catch (e) {
      logger.error('Error initializing Firebase', e)
    } finally {
      FirebaseWebUtil.#resolveReady(FirebaseWebUtil.#analytics)
    }
  }

  /**
   * Resolves with the Analytics instance once initialization has completed, or null if analytics is unavailable. Resolves immediately if init() has already settled.
   * @returns {Promise<import('firebase/analytics').Analytics|null>}
   */
  static getAnalyticInstance () {
    return FirebaseWebUtil.#ready
  }
}

export default FirebaseWebUtil
